import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import NotificationApi from '../api/notifications';
import systemNotificationService from '../services/systemNotificationService';
import { Notification, NotificationPriority } from '../types/notification';

// Central platform items arrive with their own shape — normalise into the
// local Notification model so one bell renders both feeds.
const centralPriority = (severity?: string): NotificationPriority => {
  switch (severity) {
    case 'critical': return 'critical';
    case 'warning': return 'high';
    default: return 'medium';
  }
};

const centralIcon = (type?: string, severity?: string): string => {
  if (severity === 'critical') return '🔥';
  switch (type) {
    case 'maintenance': return '🔧';
    case 'incident': return '⚠️';
    case 'patch': return '🩹';
    case 'release': return '🚀';
    case 'security': return '🔒';
    default: return '📢';
  }
};

const normalizeCentral = (n: any): Notification => ({
  _id: `central:${n._id}`,
  centralId: n._id,
  recipientId: '',
  recipientType: 'all',
  type: 'announcement',
  priority: centralPriority(n.severity),
  title: n.title,
  message: n.message,
  icon: centralIcon(n.type, n.severity),
  actionUrl: n.actionUrl,
  actionLabel: n.actionLabel,
  read: Boolean(n.userInteraction?.read),
  dismissed: false,
  requiresAction: false,
  actionResolved: true,
  preventDismissal: n.isDismissible === false,
  createdBy: 'System',
  createdByType: 'system',
  createdAt: n.publishedAt || n.createdAt || new Date().toISOString(),
  isGlobal: true,
  isCentral: true,
});

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refreshNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  getCriticalNotifications: () => Notification[];
  getUnresolvedActionNotifications: () => Notification[];
  getHighPriorityNotifications: () => Notification[];
}

// Identity is carried by the session token — context fields are ignored
// server-side but required by the service signature.
const CENTRAL_CONTEXT = { clientId: 'revo', appId: 'utilities-crm-live' };

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Central platform feed — session-token auth minted server-side. Failures
  // fail open (empty list) so a central outage never hides local alerts.
  const fetchCentral = useCallback(async (): Promise<Notification[]> => {
    try {
      const data = await systemNotificationService.getActiveNotifications(CENTRAL_CONTEXT);
      return (data.notifications || [])
        .filter((n: any) => n.visibilityLevel === 'bell' && !n.userInteraction?.dismissed)
        .map(normalizeCentral);
    } catch {
      return [];
    }
  }, []);

  // Lightweight polling: only check unread count
  const refreshUnreadCount = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const [localCount, central] = await Promise.all([
        NotificationApi.getUnreadCount(),
        fetchCentral(),
      ]);
      setUnreadCount(localCount + central.filter((n) => !n.read).length);
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Error fetching unread count:', err);
      }
    }
  }, [fetchCentral]);

  // Full refresh: fetch all notifications (called on demand)
  const refreshNotifications = useCallback(async () => {
    // Only fetch if user is authenticated (token exists)
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [notifs, localCount, central] = await Promise.all([
        NotificationApi.getNotifications(),
        NotificationApi.getUnreadCount(),
        fetchCentral(),
      ]);

      const merged = [...notifs, ...central].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setNotifications(merged);
      setUnreadCount(localCount + central.filter((n) => !n.read).length);
    } catch (err: any) {
      // Silently fail on 401 errors (user not authenticated)
      if (err.response?.status === 401) {
        setNotifications([]);
        setUnreadCount(0);
      } else {
        console.error('Error fetching notifications:', err);
        setError(err.message || 'Failed to fetch notifications');
      }
    } finally {
      setLoading(false);
    }
  }, [fetchCentral]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const target = notifications.find((n) => String(n._id) === String(notificationId));
      if (target?.isCentral) {
        await systemNotificationService.markAsRead(target.centralId!, CENTRAL_CONTEXT);
      } else {
        await NotificationApi.markAsRead(notificationId);
      }

      // Update local state - convert both to strings for comparison
      setNotifications(prev =>
        prev.map(n => String(n._id) === String(notificationId) ? { ...n, read: true, readAt: new Date().toISOString() } : n)
      );

      setUnreadCount(prev => Math.max(0, prev - (target && !target.read ? 1 : 0)));
    } catch (err: any) {
      console.error('Error marking notification as read:', err);
      throw err;
    }
  }, [notifications]);

  const markAllAsRead = useCallback(async () => {
    try {
      const centralUnread = notifications.filter((n) => n.isCentral && !n.read);
      await Promise.all([
        NotificationApi.markAllAsRead(),
        ...centralUnread.map((n) => systemNotificationService.markAsRead(n.centralId!, CENTRAL_CONTEXT)),
      ]);

      // Update local state
      setNotifications(prev =>
        prev.map(n => n.preventDismissal ? n : { ...n, read: true, readAt: new Date().toISOString() })
      );

      setUnreadCount(prev =>
        Math.max(0, prev - notifications.filter((n) => !n.read && !n.preventDismissal).length)
      );
    } catch (err: any) {
      console.error('Error marking all as read:', err);
      throw err;
    }
  }, [notifications]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      const target = notifications.find((n) => String(n._id) === String(notificationId));
      if (target?.isCentral) {
        await systemNotificationService.dismiss(target.centralId!, CENTRAL_CONTEXT);
      } else {
        await NotificationApi.deleteNotification(notificationId);
      }

      // Update local state
      setNotifications(prev => prev.filter(n => n._id !== notificationId));

      if (target && !target.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err: any) {
      console.error('Error deleting notification:', err);
      throw err;
    }
  }, [notifications]);

  const getCriticalNotifications = useCallback(() => {
    return notifications.filter(n => n.priority === 'critical' && !n.read);
  }, [notifications]);

  const getUnresolvedActionNotifications = useCallback(() => {
    return notifications.filter(n => n.requiresAction && !n.actionResolved);
  }, [notifications]);

  const getHighPriorityNotifications = useCallback(() => {
    // Return high/critical priority notifications that are unread or require action
    return notifications.filter(n =>
      (n.priority === 'high' || n.priority === 'critical') &&
      (!n.read || (n.requiresAction && !n.actionResolved))
    );
  }, [notifications]);

  // Initial load
  useEffect(() => {
    refreshNotifications();
  }, [refreshNotifications]);

  // Lightweight polling: only check unread count with staggered intervals
  useEffect(() => {
    // Stagger polling: random delay between 3-5 minutes per user
    const baseInterval = 3 * 60 * 1000; // 3 minutes
    const randomOffset = Math.random() * 2 * 60 * 1000; // 0-2 minutes random
    const pollInterval = baseInterval + randomOffset; // 3-5 minutes total

    const interval = setInterval(() => {
      refreshUnreadCount(); // Only fetch count, not full notifications
    }, pollInterval);

    return () => clearInterval(interval);
  }, [refreshUnreadCount]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    error,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    getCriticalNotifications,
    getUnresolvedActionNotifications,
    getHighPriorityNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
